import React, { useState, useEffect, useMemo } from "react";
import {
  Link2,
  ExternalLink,
  Shield,
  ShieldOff,
  TrendingUp,
  TrendingDown,
  Globe,
  RefreshCw,
  Trash2,
  Search,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader,
  HelpCircle,
  BarChart3,
  Building2,
  MapPin,
  Bell,
  Send,
  Eye,
  Filter,
  Clock,
  ChevronRight,
  Plus,
  X,
} from "lucide-react";
import api from "../api/client";

const STATUS_CONFIG = {
  unverified: { label: "Unverified", color: "text-blue-400", bg: "bg-blue-900/20", icon: HelpCircle },
  active: { label: "Active", color: "text-green-400", bg: "bg-green-900/20", icon: CheckCircle },
  lost: { label: "Lost", color: "text-red-400", bg: "bg-red-900/20", icon: XCircle },
  broken: { label: "Broken", color: "text-amber-400", bg: "bg-amber-900/20", icon: AlertCircle },
  noindex: { label: "Noindex", color: "text-gray-400", bg: "bg-gray-800/40", icon: ShieldOff },
};

const SUBMISSION_STATUS = {
  not_submitted: { label: "Not Submitted", color: "text-gray-500" },
  pending: { label: "Pending", color: "text-blue-400" },
  submitted: { label: "Submitted", color: "text-amber-400" },
  approved: { label: "Approved", color: "text-green-400" },
  rejected: { label: "Rejected", color: "text-red-400" },
  live: { label: "Live", color: "text-emerald-400" },
};

const CITATION_STATUS = {
  found: { label: "Found", color: "text-blue-400", bg: "bg-blue-900/20" },
  verified: { label: "Verified", color: "text-green-400", bg: "bg-green-900/20" },
  inconsistent: { label: "Inconsistent", color: "text-amber-400", bg: "bg-amber-900/20" },
  missing: { label: "Missing", color: "text-gray-500", bg: "bg-gray-800/40" },
};

const ALERT_TYPE_CONFIG = {
  new_backlink: { label: "New", color: "text-green-400", bg: "bg-green-900/20", icon: TrendingUp },
  lost_backlink: { label: "Lost", color: "text-red-400", bg: "bg-red-900/20", icon: TrendingDown },
  da_change: { label: "DA Change", color: "text-blue-400", bg: "bg-blue-900/20", icon: BarChart3 },
  broken_link: { label: "Broken", color: "text-amber-400", bg: "bg-amber-900/20", icon: AlertCircle },
};

function DaBar({ value }) {
  const color =
    value >= 60 ? "bg-green-500" : value >= 30 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-dark-50 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
      <span className="text-xs font-mono">{value}</span>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={16} className={color} />}
      </div>
      <div className="text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function AnchorChart({ anchors }) {
  if (!anchors || anchors.length === 0) return null;
  const max = Math.max(...anchors.map((a) => a.count));
  return (
    <div className="space-y-2">
      {anchors.map((a, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-32 text-xs text-gray-400 truncate" title={a.anchor_text}>
            {a.anchor_text || "(empty)"}
          </div>
          <div className="flex-1 h-4 bg-dark-50 rounded overflow-hidden">
            <div
              className="h-full bg-copper/60 rounded"
              style={{ width: `${(a.count / max) * 100}%` }}
            />
          </div>
          <span className="text-xs font-mono text-gray-400 w-8 text-right">{a.count}</span>
        </div>
      ))}
    </div>
  );
}

/* ======== TAB: OVERVIEW (existing backlink tracker) ======== */
function OverviewTab({ siteId }) {
  const [backlinks, setBacklinks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [followFilter, setFollowFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("first_seen");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ source_url: "", target_url: "", source: "manual" });
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    setError("");
    Promise.all([
      api.get(`/websites/${siteId}/backlinks/`),
      api.get(`/websites/${siteId}/backlinks/summary/`),
      api.get(`/websites/${siteId}/backlinks/snapshots/?days=30`),
    ])
      .then(([blRes, sumRes, snapRes]) => {
        setBacklinks(blRes.data.results || blRes.data);
        setSummary(sumRes.data);
        setSnapshots(snapRes.data.results || snapRes.data);
      })
      .catch((err) => {
        if (err.response?.status !== 401) setError("Failed to load backlink data");
      })
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => {
    if (!scanning) return;
    const interval = setInterval(() => {
      if (!siteId) return;
      Promise.all([
        api.get(`/websites/${siteId}/backlinks/`),
        api.get(`/websites/${siteId}/backlinks/summary/`),
      ]).then(([blRes, sumRes]) => {
        setBacklinks(blRes.data.results || blRes.data);
        setSummary(sumRes.data);
      });
    }, 8000);
    return () => clearInterval(interval);
  }, [scanning, siteId]);

  const handleScan = () => {
    if (!siteId) return;
    setScanning(true);
    api
      .post(`/websites/${siteId}/backlinks/scan/`)
      .then(() => setTimeout(() => setScanning(false), 60000))
      .catch(() => { setScanning(false); setError("Failed to start scan"); });
  };

  const handleDelete = (id) => {
    api.delete(`/websites/${siteId}/backlinks/${id}/`).then(() => {
      setBacklinks((prev) => prev.filter((b) => b.id !== id));
    });
  };

  const handleAddBacklink = () => {
    if (!addForm.source_url) { setAddError("Source URL is required."); return; }
    setAdding(true);
    setAddError("");
    api.post(`/websites/${siteId}/backlinks/`, addForm)
      .then((res) => {
        setBacklinks((prev) => [res.data, ...prev]);
        setShowAddModal(false);
        setAddForm({ source_url: "", target_url: "", source: "manual" });
      })
      .catch((err) => {
        setAddError(err.response?.data?.error || err.response?.data?.source_url?.[0] || "Failed to add backlink.");
      })
      .finally(() => setAdding(false));
  };

  const filteredBacklinks = useMemo(() => {
    let list = [...backlinks];
    if (statusFilter !== "all") list = list.filter((b) => b.status === statusFilter);
    if (followFilter === "follow") list = list.filter((b) => b.is_follow);
    if (followFilter === "nofollow") list = list.filter((b) => !b.is_follow);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (b) =>
          b.source_domain.toLowerCase().includes(q) ||
          b.anchor_text.toLowerCase().includes(q) ||
          b.source_url.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [backlinks, statusFilter, followFilter, searchQuery, sortField, sortDir]);

  const totalPages = Math.ceil(filteredBacklinks.length / perPage);
  const paginatedBacklinks = filteredBacklinks.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="text-gray-600" />;
    return sortDir === "asc" ? <ChevronUp size={12} className="text-copper" /> : <ChevronDown size={12} className="text-copper" />;
  };

  const velocityData = useMemo(() => {
    if (!snapshots || snapshots.length === 0) return [];
    return [...snapshots].reverse().slice(-14);
  }, [snapshots]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-copper" size={24} />
        <span className="ml-3 text-gray-400">Loading backlinks...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with scan + add buttons */}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-dark-300 border border-dark-50 text-gray-300 rounded-lg font-medium text-sm hover:border-copper/50 hover:text-copper transition-colors"
        >
          <Plus size={16} />
          Add Backlink
        </button>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors"
        >
          {scanning ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          {scanning ? "Scanning..." : "Scan Backlinks"}
        </button>
      </div>

      {/* Add Backlink Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-dark-300 border border-dark-50 rounded-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Add Backlink</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
            </div>
            {addError && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{addError}</div>}
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Source URL <span className="text-red-400">*</span></label>
              <input
                type="url"
                placeholder="https://example.com/page-linking-to-you"
                value={addForm.source_url}
                onChange={(e) => setAddForm((f) => ({ ...f, source_url: e.target.value }))}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Target URL <span className="text-gray-600">(optional, defaults to homepage)</span></label>
              <input
                type="url"
                placeholder="https://yoursite.com/target-page"
                value={addForm.target_url}
                onChange={(e) => setAddForm((f) => ({ ...f, target_url: e.target.value }))}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Type</label>
              <select
                value={addForm.source}
                onChange={(e) => setAddForm((f) => ({ ...f, source: e.target.value }))}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper/50 focus:outline-none"
              >
                <option value="manual">Manual</option>
                <option value="syndication">Syndication</option>
                <option value="directory">Directory</option>
                <option value="guest_post">Guest Post</option>
                <option value="citation">Citation</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
              <button
                onClick={handleAddBacklink}
                disabled={adding}
                className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors"
              >
                {adding ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
                {adding ? "Adding..." : "Add Backlink"}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 text-sm text-red-400">{error}</div>
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Backlinks" value={summary.active} sub={`${summary.lost} lost`} icon={Link2} />
          <StatCard label="Referring Domains" value={summary.referring_domains} icon={Globe} color="text-blue-400" />
          <StatCard
            label="Follow / Nofollow"
            value={`${summary.follow_count} / ${summary.nofollow_count}`}
            sub={summary.follow_count + summary.nofollow_count > 0 ? `${Math.round((summary.follow_count / (summary.follow_count + summary.nofollow_count)) * 100)}% dofollow` : "—"}
            icon={Shield}
            color="text-green-400"
          />
          <StatCard label="Link Velocity" value={`+${summary.link_velocity_7d}`} sub="new links / 7 days" icon={TrendingUp} color="text-purple-400" />
        </div>
      )}

      {summary && (summary.anchor_distribution?.length > 0 || summary.top_referring_domains?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {summary.anchor_distribution?.length > 0 && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                <BarChart3 size={14} /> Anchor Text Distribution
              </h3>
              <AnchorChart anchors={summary.anchor_distribution} />
            </div>
          )}
          {summary.top_referring_domains?.length > 0 && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                <Globe size={14} /> Top Referring Domains
              </h3>
              <div className="space-y-2">
                {summary.top_referring_domains.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300 truncate max-w-[200px]" title={d.source_domain}>{d.source_domain}</span>
                    <div className="flex items-center gap-3">
                      <DaBar value={Math.round(d.avg_da || 0)} />
                      <span className="text-xs text-gray-500 font-mono w-12 text-right">{d.count} link{d.count !== 1 ? "s" : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {velocityData.length > 1 && (
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <TrendingUp size={14} /> Backlink Growth (Last 14 Days)
          </h3>
          <div className="flex items-end gap-1 h-24">
            {velocityData.map((snap, i) => {
              const max = Math.max(...velocityData.map((s) => s.active_backlinks || 1));
              const h = ((snap.active_backlinks || 0) / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center group relative">
                  <div className="w-full bg-copper/40 hover:bg-copper/70 rounded-t transition-colors min-h-[2px]" style={{ height: `${Math.max(h, 2)}%` }} />
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-dark-50 text-xs text-gray-300 px-2 py-1 rounded whitespace-nowrap z-10">
                    {snap.date}: {snap.active_backlinks} active
                    {snap.new_backlinks > 0 && ` (+${snap.new_backlinks})`}
                    {snap.lost_backlinks > 0 && ` (-${snap.lost_backlinks})`}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-gray-600">
            <span>{velocityData[0]?.date}</span>
            <span>{velocityData[velocityData.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Filters + Search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden">
          {[{ key: "all", label: "All" }, { key: "unverified", label: "Unverified" }, { key: "active", label: "Active" }, { key: "lost", label: "Lost" }, { key: "broken", label: "Broken" }].map((f) => (
            <button key={f.key} onClick={() => { setStatusFilter(f.key); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === f.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden">
          {[{ key: "all", label: "All Links" }, { key: "follow", label: "Follow" }, { key: "nofollow", label: "Nofollow" }].map((f) => (
            <button key={f.key} onClick={() => { setFollowFilter(f.key); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${followFilter === f.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder="Search domain, anchor text..." value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="w-full bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-3 py-1.5 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
        </div>
        <span className="text-xs text-gray-500 font-mono">{filteredBacklinks.length} backlink{filteredBacklinks.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Backlinks Table */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-50 text-left">
                <th className="px-4 py-3 text-xs text-gray-500 font-medium cursor-pointer select-none" onClick={() => toggleSort("source_domain")}>
                  <span className="flex items-center gap-1">Source Domain <SortIcon field="source_domain" /></span>
                </th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Target</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium cursor-pointer select-none" onClick={() => toggleSort("anchor_text")}>
                  <span className="flex items-center gap-1">Anchor Text <SortIcon field="anchor_text" /></span>
                </th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium cursor-pointer select-none" onClick={() => toggleSort("domain_authority")}>
                  <span className="flex items-center gap-1">DA <SortIcon field="domain_authority" /></span>
                </th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Type</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium cursor-pointer select-none" onClick={() => toggleSort("status")}>
                  <span className="flex items-center gap-1">Status <SortIcon field="status" /></span>
                </th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium cursor-pointer select-none" onClick={() => toggleSort("first_seen")}>
                  <span className="flex items-center gap-1">First Seen <SortIcon field="first_seen" /></span>
                </th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedBacklinks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    {backlinks.length === 0 ? (
                      <div>
                        <Link2 size={32} className="mx-auto mb-3 text-gray-600" />
                        <p>No backlinks found yet.</p>
                        <p className="text-xs mt-1">Click "Scan Backlinks" to discover links pointing to your site.</p>
                      </div>
                    ) : "No backlinks match your filters."}
                  </td>
                </tr>
              ) : paginatedBacklinks.map((bl) => {
                const st = STATUS_CONFIG[bl.status] || STATUS_CONFIG.active;
                const StIcon = st.icon;
                return (
                  <tr key={bl.id} className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Globe size={14} className="text-gray-500 shrink-0" />
                        <a href={bl.source_url} target="_blank" rel="noopener noreferrer" className="text-copper hover:underline truncate max-w-[200px]" title={bl.source_url}>{bl.source_domain}</a>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-400 text-xs truncate max-w-[150px] block" title={bl.target_url}>{bl.target_url.replace(/https?:\/\/[^/]+/, "")}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-300 truncate max-w-[180px] block" title={bl.anchor_text}>{bl.anchor_text || <span className="text-gray-600 italic">—</span>}</span>
                    </td>
                    <td className="px-4 py-3"><DaBar value={bl.domain_authority} /></td>
                    <td className="px-4 py-3">
                      {bl.is_follow ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-400"><Shield size={12} /> Follow</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500"><ShieldOff size={12} /> Nofollow</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs ${st.color}`}><StIcon size={12} /> {st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono whitespace-nowrap">{bl.first_seen ? new Date(bl.first_seen).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(bl.id)} className="text-gray-600 hover:text-red-400 transition-colors" title="Delete"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-dark-50">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-xs bg-dark-50 rounded disabled:opacity-30 hover:bg-dark-50/80 transition-colors">Prev</button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-xs bg-dark-50 rounded disabled:opacity-30 hover:bg-dark-50/80 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {summary && summary.avg_domain_authority > 0 && (
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <HelpCircle size={14} /> Average Domain Authority of your backlink profile:
            </div>
            <DaBar value={Math.round(summary.avg_domain_authority)} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ======== TAB: DIRECTORIES ======== */
function DirectoriesTab({ siteId }) {
  const [directories, setDirectories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/backlink-builder/directories/`)
      .then((res) => setDirectories(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleStatusUpdate = (dirId, submissionId, newStatus) => {
    setSubmitting(dirId);
    if (submissionId) {
      api.patch(`/websites/${siteId}/backlink-builder/submissions/${submissionId}/`, { status: newStatus })
        .then(() => {
          setDirectories((prev) =>
            prev.map((d) =>
              d.id === dirId
                ? { ...d, submission_status: { ...d.submission_status, status: newStatus } }
                : d
            )
          );
        })
        .finally(() => setSubmitting(null));
    } else {
      api.post(`/websites/${siteId}/backlink-builder/submissions/`, { directory: dirId, status: newStatus })
        .then((res) => {
          setDirectories((prev) =>
            prev.map((d) =>
              d.id === dirId
                ? { ...d, submission_status: { id: res.data.id, status: newStatus } }
                : d
            )
          );
        })
        .finally(() => setSubmitting(null));
    }
  };

  const filtered = useMemo(() => {
    let list = [...directories];
    if (categoryFilter !== "all") list = list.filter((d) => d.category === categoryFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((d) => d.name.toLowerCase().includes(q) || d.url.toLowerCase().includes(q));
    }
    return list;
  }, [directories, categoryFilter, searchQuery]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading directories...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Directories" value={directories.length} icon={Building2} />
        <StatCard label="Submitted" value={directories.filter((d) => d.submission_status && d.submission_status.status !== "not_submitted").length} icon={Send} color="text-blue-400" />
        <StatCard label="Live Listings" value={directories.filter((d) => d.submission_status?.status === "live").length} icon={CheckCircle} color="text-green-400" />
        <StatCard label="Not Submitted" value={directories.filter((d) => !d.submission_status).length} icon={Clock} color="text-gray-400" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden">
          {[{ key: "all", label: "All" }, { key: "general", label: "General" }, { key: "local", label: "Local" }, { key: "industry", label: "Industry" }, { key: "niche", label: "Niche" }].map((f) => (
            <button key={f.key} onClick={() => setCategoryFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${categoryFilter === f.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder="Search directories..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-3 py-1.5 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
        </div>
      </div>

      {/* Directories Table */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-50 text-left">
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Directory</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Category</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">DA</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Cost</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Status</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">No directories found.</td></tr>
              ) : filtered.map((dir) => {
                const sub = dir.submission_status;
                const subStatus = sub ? SUBMISSION_STATUS[sub.status] || SUBMISSION_STATUS.not_submitted : SUBMISSION_STATUS.not_submitted;
                const currentStatus = sub?.status || "not_submitted";
                return (
                  <tr key={dir.id} className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <a href={dir.submission_url || dir.url} target="_blank" rel="noopener noreferrer" className="text-copper hover:underline font-medium">{dir.name}</a>
                        {dir.notes && <p className="text-xs text-gray-500 mt-0.5">{dir.notes}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded bg-dark-50 text-gray-400 capitalize">{dir.category}</span>
                    </td>
                    <td className="px-4 py-3"><DaBar value={dir.domain_authority} /></td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${dir.is_free ? "text-green-400" : "text-amber-400"}`}>{dir.is_free ? "Free" : "Paid"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${subStatus.color}`}>{subStatus.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {currentStatus === "not_submitted" && (
                          <button
                            onClick={() => handleStatusUpdate(dir.id, null, "submitted")}
                            disabled={submitting === dir.id}
                            className="text-xs px-2 py-1 bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors disabled:opacity-50"
                          >
                            {submitting === dir.id ? "..." : "Mark Submitted"}
                          </button>
                        )}
                        {currentStatus === "submitted" && (
                          <>
                            <button onClick={() => handleStatusUpdate(dir.id, sub.id, "approved")} disabled={submitting === dir.id}
                              className="text-xs px-2 py-1 bg-green-900/30 text-green-400 rounded hover:bg-green-900/50 transition-colors disabled:opacity-50">Approved</button>
                            <button onClick={() => handleStatusUpdate(dir.id, sub.id, "rejected")} disabled={submitting === dir.id}
                              className="text-xs px-2 py-1 bg-red-900/30 text-red-400 rounded hover:bg-red-900/50 transition-colors disabled:opacity-50">Rejected</button>
                          </>
                        )}
                        {currentStatus === "approved" && (
                          <button onClick={() => handleStatusUpdate(dir.id, sub.id, "live")} disabled={submitting === dir.id}
                            className="text-xs px-2 py-1 bg-emerald-900/30 text-emerald-400 rounded hover:bg-emerald-900/50 transition-colors disabled:opacity-50">Mark Live</button>
                        )}
                        {dir.submission_url && (
                          <a href={dir.submission_url} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 text-gray-400 hover:text-white transition-colors">
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ======== TAB: CITATIONS ======== */
function CitationsTab({ siteId }) {
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetchCitations = () => {
    api.get(`/websites/${siteId}/backlink-builder/citations/`)
      .then((res) => setCitations(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    fetchCitations();
  }, [siteId]);

  const handleScan = () => {
    setScanning(true);
    api.post(`/websites/${siteId}/backlink-builder/citations/scan/`)
      .then(() => {
        setTimeout(() => { setScanning(false); fetchCitations(); }, 30000);
      })
      .catch(() => setScanning(false));
  };

  const found = citations.filter((c) => c.status !== "missing");
  const missing = citations.filter((c) => c.status === "missing");
  const inconsistent = citations.filter((c) => c.status === "inconsistent");

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading citations...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Citations" value={citations.length} icon={MapPin} />
        <StatCard label="Found" value={found.length} icon={CheckCircle} color="text-green-400" />
        <StatCard label="Missing" value={missing.length} icon={XCircle} color="text-red-400" />
        <StatCard label="Inconsistent NAP" value={inconsistent.length} icon={AlertCircle} color="text-amber-400" />
      </div>

      {/* Scan button */}
      <div className="flex justify-end">
        <button onClick={handleScan} disabled={scanning}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
          {scanning ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          {scanning ? "Scanning..." : "Scan Citations"}
        </button>
      </div>

      {/* Citations list */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-50 text-left">
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Platform</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Status</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">NAP Name</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Phone</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Consistent</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Listing</th>
              </tr>
            </thead>
            <tbody>
              {citations.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                  <MapPin size={32} className="mx-auto mb-3 text-gray-600" />
                  <p>No citations found yet.</p>
                  <p className="text-xs mt-1">Click "Scan Citations" to find your business across the web.</p>
                </td></tr>
              ) : citations.map((c) => {
                const st = CITATION_STATUS[c.status] || CITATION_STATUS.missing;
                return (
                  <tr key={c.id} className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-300">{c.platform}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${st.bg} ${st.color}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{c.nap_name || "—"}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">{c.nap_phone || "—"}</td>
                    <td className="px-4 py-3">
                      {c.status === "missing" ? (
                        <span className="text-gray-600">—</span>
                      ) : c.is_consistent ? (
                        <CheckCircle size={14} className="text-green-400" />
                      ) : (
                        <AlertCircle size={14} className="text-amber-400" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.listing_url ? (
                        <a href={c.listing_url} target="_blank" rel="noopener noreferrer" className="text-copper hover:underline text-xs flex items-center gap-1">
                          View <ExternalLink size={10} />
                        </a>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ======== TAB: ALERTS ======== */
function AlertsTab({ siteId }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertSummary, setAlertSummary] = useState(null);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/backlink-builder/alerts/`),
      api.get(`/websites/${siteId}/backlink-builder/alerts/summary/`),
    ])
      .then(([alertsRes, summaryRes]) => {
        setAlerts(alertsRes.data.results || alertsRes.data);
        setAlertSummary(summaryRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleMarkAllRead = () => {
    api.post(`/websites/${siteId}/backlink-builder/alerts/mark-all-read/`)
      .then(() => {
        setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
        setAlertSummary((prev) => prev ? { ...prev, unread_total: 0, by_type: {} } : prev);
      });
  };

  const handleMarkRead = (alertId) => {
    api.patch(`/websites/${siteId}/backlink-builder/alerts/${alertId}/`, { is_read: true })
      .then(() => {
        setAlerts((prev) => prev.map((a) => a.id === alertId ? { ...a, is_read: true } : a));
      });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading alerts...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Unread Alerts" value={alertSummary?.unread_total || 0} icon={Bell} color="text-amber-400" />
        <StatCard label="New Links" value={alertSummary?.by_type?.new_backlink || 0} icon={TrendingUp} color="text-green-400" />
        <StatCard label="Lost Links" value={alertSummary?.by_type?.lost_backlink || 0} icon={TrendingDown} color="text-red-400" />
        <StatCard label="Broken Links" value={alertSummary?.by_type?.broken_link || 0} icon={AlertCircle} color="text-amber-400" />
      </div>

      {/* Mark all read */}
      {alertSummary?.unread_total > 0 && (
        <div className="flex justify-end">
          <button onClick={handleMarkAllRead}
            className="text-xs px-3 py-1.5 text-gray-400 hover:text-white border border-dark-50 rounded-lg hover:bg-dark-50/50 transition-colors">
            Mark All Read
          </button>
        </div>
      )}

      {/* Alert feed */}
      <div className="space-y-2">
        {alerts.length === 0 ? (
          <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
            <Bell size={32} className="mx-auto mb-3 text-gray-600" />
            <p>No alerts yet.</p>
            <p className="text-xs mt-1">Alerts are generated daily when backlinks change.</p>
          </div>
        ) : alerts.map((alert) => {
          const cfg = ALERT_TYPE_CONFIG[alert.alert_type] || ALERT_TYPE_CONFIG.new_backlink;
          const Icon = cfg.icon;
          return (
            <div
              key={alert.id}
              className={`bg-dark-300 rounded-xl border transition-colors p-4 flex items-start gap-3 ${
                alert.is_read ? "border-dark-50 opacity-60" : "border-copper/30"
              }`}
            >
              <div className={`p-2 rounded-lg ${cfg.bg}`}>
                <Icon size={16} className={cfg.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                  {alert.source_domain && (
                    <span className="text-xs text-gray-500">{alert.source_domain}</span>
                  )}
                </div>
                <p className="text-sm text-gray-300">{alert.message}</p>
                <span className="text-[10px] text-gray-600 mt-1 block">
                  {new Date(alert.created_at).toLocaleString()}
                </span>
              </div>
              {!alert.is_read && (
                <button onClick={() => handleMarkRead(alert.id)}
                  className="text-xs text-gray-500 hover:text-white transition-colors shrink-0">
                  <Eye size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ======== TAB: BUILD OVERVIEW ======== */
function BuildOverviewTab({ siteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/backlink-builder/overview/`)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Directories Submitted" value={data.directories.submitted} sub={`of ${data.directories.total} total`} icon={Building2} />
        <StatCard label="Live Listings" value={data.directories.approved} icon={CheckCircle} color="text-green-400" />
        <StatCard label="Guest Posts" value={data.guest_posts.published} sub={`${data.guest_posts.writing} in progress`} icon={Send} color="text-blue-400" />
        <StatCard label="Citations" value={data.citations.total} sub={`${data.citations.inconsistent} inconsistent`} icon={MapPin} color="text-amber-400" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Directory Progress */}
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-5">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <Building2 size={14} /> Directory Submissions
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Progress</span>
              <span className="text-copper font-mono">{data.directories.submitted}/{data.directories.total}</span>
            </div>
            <div className="w-full h-2 bg-dark-50 rounded-full overflow-hidden">
              <div className="h-full bg-copper rounded-full transition-all" style={{ width: `${data.directories.total ? (data.directories.submitted / data.directories.total) * 100 : 0}%` }} />
            </div>
            <div className="flex gap-4 text-xs text-gray-500">
              <span>{data.directories.approved} approved</span>
              <span>{data.directories.submitted - data.directories.approved} pending</span>
            </div>
          </div>
        </div>

        {/* Outreach Stats */}
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-5">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <Send size={14} /> Outreach Performance
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Campaigns</span>
              <span className="font-mono">{data.outreach.campaigns}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Emails Sent</span>
              <span className="font-mono">{data.outreach.total_sent}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Replies</span>
              <span className="font-mono text-green-400">{data.outreach.total_replies}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======== TAB: BUSINESS PROFILE ======== */
function BusinessProfileTab({ siteId }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/backlink-builder/profile/`)
      .then((res) => { setProfile(res.data); setForm(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleSave = () => {
    setSaving(true);
    api.patch(`/websites/${siteId}/backlink-builder/profile/`, form)
      .then((res) => { setProfile(res.data); setForm(res.data); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /></div>;

  const fields = [
    { key: "business_name", label: "Business Name", type: "text" },
    { key: "phone", label: "Phone", type: "tel" },
    { key: "email", label: "Email", type: "email" },
    { key: "address_line1", label: "Address Line 1", type: "text" },
    { key: "address_line2", label: "Address Line 2", type: "text" },
    { key: "city", label: "City", type: "text" },
    { key: "state", label: "State", type: "text" },
    { key: "zip_code", label: "ZIP Code", type: "text" },
    { key: "description", label: "Business Description", type: "textarea" },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-6">
        <h3 className="text-sm font-medium text-gray-400 mb-4">Business Information (NAP)</h3>
        <p className="text-xs text-gray-500 mb-6">This information is used for all directory submissions and citation building.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="text-xs text-gray-500 mb-1 block">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea value={form[f.key] || ""} onChange={(e) => updateField(f.key, e.target.value)} rows={3}
                  className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
              ) : (
                <input type={f.type} value={form[f.key] || ""} onChange={(e) => updateField(f.key, e.target.value)}
                  className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
            {saving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ======== TAB: GUEST POSTS ======== */
function GuestPostsTab({ siteId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/backlink-builder/guest-posts/`)
      .then((res) => setPosts(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleDiscover = () => {
    setDiscovering(true);
    api.post(`/websites/${siteId}/backlink-builder/guest-posts/discover/`)
      .then(() => {
        setTimeout(() => {
          api.get(`/websites/${siteId}/backlink-builder/guest-posts/`)
            .then((res) => setPosts(res.data.results || res.data));
          setDiscovering(false);
        }, 15000);
      })
      .catch(() => setDiscovering(false));
  };

  const handleGenerateArticle = (postId) => {
    api.post(`/websites/${siteId}/backlink-builder/guest-posts/${postId}/generate-article/`)
      .then(() => {
        setTimeout(() => {
          api.get(`/websites/${siteId}/backlink-builder/guest-posts/`)
            .then((res) => setPosts(res.data.results || res.data));
        }, 20000);
      });
  };

  const handleStatusChange = (postId, newStatus) => {
    api.patch(`/websites/${siteId}/backlink-builder/guest-posts/${postId}/`, { status: newStatus })
      .then((res) => {
        setPosts((prev) => prev.map((p) => p.id === postId ? res.data : p));
      });
  };

  const GP_STATUS = {
    pitching: { label: "Prospect", color: "text-blue-400", bg: "bg-blue-900/20" },
    writing: { label: "Writing", color: "text-amber-400", bg: "bg-amber-900/20" },
    submitted: { label: "Submitted", color: "text-purple-400", bg: "bg-purple-900/20" },
    published: { label: "Published", color: "text-green-400", bg: "bg-green-900/20" },
    rejected: { label: "Rejected", color: "text-red-400", bg: "bg-red-900/20" },
  };

  const filtered = statusFilter === "all" ? posts : posts.filter((p) => p.status === statusFilter);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Posts" value={posts.length} icon={Send} />
        <StatCard label="Published" value={posts.filter((p) => p.status === "published").length} icon={CheckCircle} color="text-green-400" />
        <StatCard label="In Progress" value={posts.filter((p) => ["pitching", "writing", "submitted"].includes(p.status)).length} icon={Clock} color="text-amber-400" />
        <StatCard label="Total Words" value={posts.reduce((sum, p) => sum + (p.word_count || 0), 0).toLocaleString()} icon={BarChart3} color="text-blue-400" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden">
          {[{ key: "all", label: "All" }, ...Object.entries(GP_STATUS).map(([k, v]) => ({ key: k, label: v.label }))].map((f) => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === f.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={handleDiscover} disabled={discovering}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
          {discovering ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
          {discovering ? "Discovering..." : "Discover Opportunities"}
        </button>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
            <Send size={32} className="mx-auto mb-3 text-gray-600" />
            <p>No guest posts yet.</p>
            <p className="text-xs mt-1">Click "Discover Opportunities" to find guest post prospects.</p>
          </div>
        ) : filtered.map((post) => {
          const st = GP_STATUS[post.status] || GP_STATUS.pitching;
          return (
            <div key={post.id} className="bg-dark-300 rounded-xl border border-dark-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${st.bg} ${st.color}`}>{st.label}</span>
                    {post.prospect_site && <span className="text-xs text-gray-500">{post.prospect_site}</span>}
                  </div>
                  <h4 className="text-sm font-medium text-gray-200 truncate">{post.title || "Untitled"}</h4>
                  {post.prospect_url && (
                    <a href={post.prospect_url} target="_blank" rel="noopener noreferrer" className="text-xs text-copper hover:underline flex items-center gap-1 mt-1">
                      {post.prospect_url.replace(/https?:\/\//, "").substring(0, 40)} <ExternalLink size={10} />
                    </a>
                  )}
                  {post.word_count > 0 && <span className="text-xs text-gray-500 mt-1 block">{post.word_count.toLocaleString()} words</span>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {post.status === "pitching" && (
                    <button onClick={() => handleGenerateArticle(post.id)}
                      className="text-xs px-2 py-1 bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors">
                      Generate Article
                    </button>
                  )}
                  {post.status === "writing" && (
                    <button onClick={() => handleStatusChange(post.id, "submitted")}
                      className="text-xs px-2 py-1 bg-purple-900/30 text-purple-400 rounded hover:bg-purple-900/50 transition-colors">
                      Mark Submitted
                    </button>
                  )}
                  {post.status === "submitted" && (
                    <>
                      <button onClick={() => handleStatusChange(post.id, "published")}
                        className="text-xs px-2 py-1 bg-green-900/30 text-green-400 rounded hover:bg-green-900/50 transition-colors">Published</button>
                      <button onClick={() => handleStatusChange(post.id, "rejected")}
                        className="text-xs px-2 py-1 bg-red-900/30 text-red-400 rounded hover:bg-red-900/50 transition-colors">Rejected</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ======== TAB: OUTREACH ======== */
function OutreachTab({ siteId }) {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: "", campaign_type: "guest_post" });
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [prospects, setProspects] = useState([]);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/backlink-builder/outreach/`),
      api.get(`/websites/${siteId}/backlink-builder/email-templates/`),
    ])
      .then(([campRes, tplRes]) => {
        setCampaigns(campRes.data.results || campRes.data);
        setTemplates(tplRes.data.results || tplRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleCreateCampaign = () => {
    api.post(`/websites/${siteId}/backlink-builder/outreach/`, newCampaign)
      .then((res) => {
        setCampaigns((prev) => [res.data, ...prev]);
        setShowNewCampaign(false);
        setNewCampaign({ name: "", campaign_type: "guest_post" });
      });
  };

  const handleExpandCampaign = (campaignId) => {
    if (expandedCampaign === campaignId) {
      setExpandedCampaign(null);
      return;
    }
    setExpandedCampaign(campaignId);
    api.get(`/websites/${siteId}/backlink-builder/outreach/${campaignId}/prospects/`)
      .then((res) => setProspects(res.data.results || res.data))
      .catch(() => setProspects([]));
  };

  const handleSendBatch = (campaignId) => {
    api.post(`/websites/${siteId}/backlink-builder/outreach/${campaignId}/send-batch/`)
      .then(() => {
        setTimeout(() => {
          api.get(`/websites/${siteId}/backlink-builder/outreach/`)
            .then((res) => setCampaigns(res.data.results || res.data));
        }, 5000);
      });
  };

  const CAMPAIGN_TYPES = {
    guest_post: "Guest Post",
    resource_page: "Resource Page",
    broken_link: "Broken Link",
    unlinked_mention: "Unlinked Mention",
    link_exchange: "Link Exchange",
    local_partnership: "Local Partnership",
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Campaigns" value={campaigns.length} icon={Send} />
        <StatCard label="Total Sent" value={campaigns.reduce((s, c) => s + c.total_sent, 0)} icon={Send} color="text-blue-400" />
        <StatCard label="Total Replies" value={campaigns.reduce((s, c) => s + c.total_replied, 0)} icon={CheckCircle} color="text-green-400" />
        <StatCard label="Templates" value={templates.length} icon={Filter} color="text-purple-400" />
      </div>

      <div className="flex justify-end">
        <button onClick={() => setShowNewCampaign(!showNewCampaign)}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 transition-colors">
          + New Campaign
        </button>
      </div>

      {showNewCampaign && (
        <div className="bg-dark-300 rounded-xl border border-copper/30 p-4 space-y-3">
          <input type="text" placeholder="Campaign name..." value={newCampaign.name}
            onChange={(e) => setNewCampaign((prev) => ({ ...prev, name: e.target.value }))}
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
          <select value={newCampaign.campaign_type}
            onChange={(e) => setNewCampaign((prev) => ({ ...prev, campaign_type: e.target.value }))}
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper/50 focus:outline-none">
            {Object.entries(CAMPAIGN_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNewCampaign(false)} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
            <button onClick={handleCreateCampaign} disabled={!newCampaign.name}
              className="px-3 py-1.5 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 disabled:opacity-50 transition-colors">Create</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {campaigns.length === 0 ? (
          <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
            <Send size={32} className="mx-auto mb-3 text-gray-600" />
            <p>No outreach campaigns yet.</p>
          </div>
        ) : campaigns.map((camp) => (
          <div key={camp.id} className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
            <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-dark-50/30 transition-colors"
              onClick={() => handleExpandCampaign(camp.id)}>
              <div className="flex items-center gap-3">
                <div className={expandedCampaign === camp.id ? "" : ""}>
                  {expandedCampaign === camp.id ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-200">{camp.name}</h4>
                  <span className="text-xs text-gray-500">{CAMPAIGN_TYPES[camp.campaign_type] || camp.campaign_type} · {camp.status}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>{camp.total_prospects} prospects</span>
                <span>{camp.total_sent} sent</span>
                <span className="text-green-400">{camp.total_replied} replies</span>
                <button onClick={(e) => { e.stopPropagation(); handleSendBatch(camp.id); }}
                  className="px-2 py-1 bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors">
                  Send Batch
                </button>
              </div>
            </div>
            {expandedCampaign === camp.id && prospects.length > 0 && (
              <div className="border-t border-dark-50 p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500">
                      <th className="pb-2">Site</th>
                      <th className="pb-2">DA</th>
                      <th className="pb-2">Contact</th>
                      <th className="pb-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prospects.map((p) => (
                      <tr key={p.id} className="border-t border-dark-50/30">
                        <td className="py-2">
                          <a href={p.site_url} target="_blank" rel="noopener noreferrer" className="text-copper hover:underline text-xs truncate block max-w-[200px]">{p.site_name || p.site_url}</a>
                        </td>
                        <td className="py-2"><DaBar value={p.da_score} /></td>
                        <td className="py-2 text-xs text-gray-400">{p.contact_email || "—"}</td>
                        <td className="py-2"><span className="text-xs text-gray-400 capitalize">{p.status.replace("_", " ")}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======== MAIN COMPONENT ======== */
const TABS = [
  { key: "build", label: "Build Overview", icon: TrendingUp },
  { key: "overview", label: "Tracker", icon: Link2 },
  { key: "profile", label: "Business Profile", icon: Building2 },
  { key: "directories", label: "Directories", icon: Building2 },
  { key: "citations", label: "Citations", icon: MapPin },
  { key: "guest-posts", label: "Guest Posts", icon: Send },
  { key: "outreach", label: "Outreach", icon: Send },
  { key: "alerts", label: "Alerts", icon: Bell },
];

export default function BacklinkTracker({ selectedSite }) {
  const [activeTab, setActiveTab] = useState("build");
  const [alertCount, setAlertCount] = useState(0);

  const siteId = selectedSite?.id;

  // Fetch unread alert count for badge
  useEffect(() => {
    if (!siteId) return;
    api.get(`/websites/${siteId}/backlink-builder/alerts/summary/`)
      .then((res) => setAlertCount(res.data.unread_total || 0))
      .catch(() => {});
  }, [siteId]);

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to view backlinks.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-mono">
          <span className="text-copper">Backlink</span> Builder
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Build, monitor, and grow your backlink profile
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors relative ${
                activeTab === tab.key
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <Icon size={16} />
              {tab.label}
              {tab.key === "alerts" && alertCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {alertCount > 9 ? "9+" : alertCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "build" && <BuildOverviewTab siteId={siteId} />}
      {activeTab === "overview" && <OverviewTab siteId={siteId} />}
      {activeTab === "profile" && <BusinessProfileTab siteId={siteId} />}
      {activeTab === "directories" && <DirectoriesTab siteId={siteId} />}
      {activeTab === "citations" && <CitationsTab siteId={siteId} />}
      {activeTab === "guest-posts" && <GuestPostsTab siteId={siteId} />}
      {activeTab === "outreach" && <OutreachTab siteId={siteId} />}
      {activeTab === "alerts" && <AlertsTab siteId={siteId} />}
    </div>
  );
}
